/**
 * SEP 5.b.2 — Native rectangle selection state machine (PURE).
 *
 * No React, no DOM. GridViewer feeds world coordinates into these
 * functions; tests exercise the same functions directly.
 *
 * Contract (spec §G + §H + §K):
 *  - `mode: "browse" | "select"` is EXPLICIT. Switching to "browse"
 *    cancels any in-progress draft (committed selection is preserved).
 *  - A draft MUST start INSIDE the grid. `beginDraft` for a world point
 *    outside [0,1000)² is a no-op — NO draft is created (§G "start-fuori").
 *  - During a valid draft, the CURRENT world coord may drift outside the
 *    grid; commit clamps ONLY the endpoint to [0,999]. The start cell
 *    (already inside) is never re-clamped.
 *  - A committed rectangle ALWAYS satisfies: x≥0, y≥0, x+w≤1000, y+h≤1000
 *    and w≥1, h≥1 (§G invariants).
 *  - A single click (start == cur) commits a 1×1 selection (§I).
 */
import type { GridSelection } from "../lib/grid-selection";

export type SelectionMode = "browse" | "select";

export interface RectDraft {
  /** Start cell index (inclusive), guaranteed in [0, GRID_SIZE-1]. */
  startCellX: number;
  startCellY: number;
  /** Raw, unclamped current world coordinate (float). */
  curWorldX: number;
  curWorldY: number;
}

export interface RectSelectionState {
  mode: SelectionMode;
  draft: RectDraft | null;
  committed: GridSelection | null;
}

export const GRID_SIZE = 1000;

export function initRectSelection(
  mode: SelectionMode = "browse",
): RectSelectionState {
  return { mode, draft: null, committed: null };
}

/**
 * A world point (wx, wy) is INSIDE the grid iff wx∈[0,1000) and wy∈[0,1000).
 * Non-finite values are treated as outside.
 */
export function isInsideGrid(worldX: number, worldY: number): boolean {
  return (
    Number.isFinite(worldX) &&
    Number.isFinite(worldY) &&
    worldX >= 0 &&
    worldY >= 0 &&
    worldX < GRID_SIZE &&
    worldY < GRID_SIZE
  );
}

/** Floor world coord to cell index and clamp to [0, GRID_SIZE-1]. */
export function clampCell(v: number): number {
  if (!Number.isFinite(v)) return 0;
  return Math.max(0, Math.min(GRID_SIZE - 1, Math.floor(v)));
}

/**
 * Change mode. Switching TO "browse" cancels any active draft.
 * The committed selection is ALWAYS preserved across mode changes.
 */
export function setMode(
  state: RectSelectionState,
  mode: SelectionMode,
): RectSelectionState {
  if (state.mode === mode) return state;
  if (mode === "browse") {
    return { mode, draft: null, committed: state.committed };
  }
  return { ...state, mode };
}

/**
 * §G — Begin a fresh draft. Rules:
 *  1. Only in "select" mode. In "browse" this is a no-op.
 *  2. The START world point MUST be inside [0,1000)². Otherwise NO draft
 *     is created (spec: "start fuori grid → nessuna selection").
 *  3. Any existing draft is REPLACED.
 *  4. The committed selection is untouched by beginDraft.
 */
export function beginDraft(
  state: RectSelectionState,
  worldX: number,
  worldY: number,
): RectSelectionState {
  if (state.mode !== "select") return state;
  if (!isInsideGrid(worldX, worldY)) return state;
  const startCellX = clampCell(worldX);
  const startCellY = clampCell(worldY);
  return {
    ...state,
    draft: {
      startCellX,
      startCellY,
      curWorldX: worldX,
      curWorldY: worldY,
    },
  };
}

/** Update the draft's current world position. No-op if no draft. */
export function updateDraft(
  state: RectSelectionState,
  worldX: number,
  worldY: number,
): RectSelectionState {
  if (!state.draft) return state;
  return {
    ...state,
    draft: { ...state.draft, curWorldX: worldX, curWorldY: worldY },
  };
}

/**
 * Compute the clamped rectangle described by a draft.
 * Invariants guaranteed on the return value:
 *   x∈[0,999], y∈[0,999], w≥1, h≥1, x+w≤1000, y+h≤1000.
 */
export function computeSelectionFromDraft(d: RectDraft): GridSelection {
  const sx = d.startCellX;
  const sy = d.startCellY;
  const cx = clampCell(d.curWorldX);
  const cy = clampCell(d.curWorldY);
  const x = Math.min(sx, cx);
  const y = Math.min(sy, cy);
  const w = Math.abs(cx - sx) + 1;
  const h = Math.abs(cy - sy) + 1;
  return { x, y, w, h };
}

/** Overlay preview: the selection that a commit would produce right now. */
export function deriveDraftPreview(
  state: RectSelectionState,
): GridSelection | null {
  return state.draft ? computeSelectionFromDraft(state.draft) : null;
}

/**
 * Commit the current draft into `committed` and clear the draft.
 * No-op if there is no draft.
 */
export function commitDraft(state: RectSelectionState): RectSelectionState {
  if (!state.draft) return state;
  const committed = computeSelectionFromDraft(state.draft);
  return { ...state, draft: null, committed };
}

/** Cancel the draft (pointercancel / Escape). Committed unchanged. */
export function cancelDraft(state: RectSelectionState): RectSelectionState {
  if (!state.draft) return state;
  return { ...state, draft: null };
}

/** Clear the committed selection (draft unchanged). */
export function clearCommitted(state: RectSelectionState): RectSelectionState {
  if (state.committed == null) return state;
  return { ...state, committed: null };
}
